/**
 * Self-test suite: Pinned messages (cross-device sync)
 *
 * Covers four halves, mirroring PinnedMessageSelfTest.swift:
 *
 *   A. Codec — round-trip pin/unpin; isPinEvent prefix discrimination;
 *      tryDecode nil for non-pin; malformed base64, unsupported schema,
 *      empty channelId, and empty messageId all rejected.
 *
 *   B. Store mechanics — pin/isPinned; unpin clears; re-pin idempotent;
 *      out-of-order unpin and out-of-order pin replay rejected;
 *      multiple channels independent; two messages in same channel
 *      independent; clearChannel scoped; resetAll clears everything;
 *      persistence across store instances.
 *
 *   C. Server-rebind resistance — forged channelId scoped; stale unpin
 *      ignored; 51st pin evicts oldest by pinnedTs.
 *
 *   D. Sorting + listing — descending by pinnedTs; only currently-pinned.
 *
 * Suite name: "Pinned messages (cross-device sync)"
 * Corresponds to B.10 in Signal_standard_web.md.
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  PinnedMessageCodec,
  PinnedMessageCodecError,
} from "./PinnedMessage";
import type { PinnedMessagePayload } from "./PinnedMessage";
import { PinnedMessageStore, MAX_PINS_PER_CHANNEL } from "./PinnedMessageStore";

// ---------------------------------------------------------------------------
// Fixtures
// ---------------------------------------------------------------------------

/** Unique IDB database per test so they cannot pollute one another. */
function freshStore(): PinnedMessageStore {
  return new PinnedMessageStore(`kaether-pintest-${crypto.randomUUID()}`);
}

function dropDb(name: string): Promise<void> {
  return new Promise((resolve) => {
    const req = indexedDB.deleteDatabase(name);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
    req.onblocked = () => resolve();
  });
}

/** Build a fresh store backed by a known-name DB (for persistence tests). */
function namedStore(name: string): PinnedMessageStore {
  return new PinnedMessageStore(name);
}

function b64url(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let b64 = btoa(String.fromCharCode(...bytes));
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// ---------------------------------------------------------------------------
// A. Codec
// ---------------------------------------------------------------------------

async function testCodecRoundTripPin(): Promise<TestCase> {
  const name = "codec round-trips pin event";
  try {
    const p: PinnedMessagePayload = {
      schema: 1,
      channelId: "ch",
      messageId: "m1",
      action: "pin",
      ts: 1_700_000_000,
    };
    const encoded = PinnedMessageCodec.encode(p);
    const decoded = PinnedMessageCodec.decode(encoded);
    if (
      decoded.schema !== p.schema ||
      decoded.channelId !== p.channelId ||
      decoded.messageId !== p.messageId ||
      decoded.action !== p.action ||
      decoded.ts !== p.ts
    ) {
      return fail(name, `got ${JSON.stringify(decoded)}, want ${JSON.stringify(p)}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testCodecRoundTripUnpin(): Promise<TestCase> {
  const name = "codec round-trips unpin event";
  try {
    const p: PinnedMessagePayload = {
      schema: 1,
      channelId: "ch",
      messageId: "m1",
      action: "unpin",
      ts: 1_700_000_001,
    };
    const encoded = PinnedMessageCodec.encode(p);
    const decoded = PinnedMessageCodec.decode(encoded);
    if (decoded.action !== "unpin" || decoded.ts !== p.ts) {
      return fail(name, `got ${JSON.stringify(decoded)}, want ${JSON.stringify(p)}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testIsPinEventDistinguishesOtherEnvelopes(): Promise<TestCase> {
  const name =
    "isPinEvent distinguishes from chat/envelope/receipt/tombstone/reply";
  try {
    const p: PinnedMessagePayload = {
      schema: 1,
      channelId: "ch",
      messageId: "m1",
      action: "pin",
      ts: 1,
    };
    const positive = PinnedMessageCodec.isPinEvent(
      PinnedMessageCodec.encode(p),
    );
    const negChat = PinnedMessageCodec.isPinEvent("hello world");
    const negEnv = PinnedMessageCodec.isPinEvent("kaether:e2ee:v1:foo");
    const negReceipt = PinnedMessageCodec.isPinEvent("kaether:receipt:v1:abc");
    const negTomb = PinnedMessageCodec.isPinEvent(
      "kaether:tombstone:v1:abc",
    );
    const negReply = PinnedMessageCodec.isPinEvent("kaether:reply:v1:abc");
    const ok =
      positive && !negChat && !negEnv && !negReceipt && !negTomb && !negReply;
    if (!ok) {
      return fail(
        name,
        `pos=${positive} chat=${negChat} env=${negEnv} rcpt=${negReceipt} tomb=${negTomb} reply=${negReply}`,
      );
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testTryDecodeReturnsNilForUserMessage(): Promise<TestCase> {
  const name = "tryDecode returns null for non-pin plaintext";
  try {
    const r = PinnedMessageCodec.tryDecode("just a chat message");
    if (r !== null) {
      return fail(name, `expected null, got ${JSON.stringify(r)}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testMalformedBase64Rejected(): Promise<TestCase> {
  const name = "malformed base64 in pin event is rejected";
  try {
    const bad = PinnedMessageCodec.prefix + "@@@not-base64@@@";
    try {
      PinnedMessageCodec.decode(bad);
      return fail(name, "decode did not throw");
    } catch (e) {
      if (e instanceof PinnedMessageCodecError && e.kind === "malformed") {
        return pass(name);
      }
      return fail(
        name,
        `wrong error: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
  } catch (e) {
    return fail(name, `setup threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testUnsupportedSchemaRejected(): Promise<TestCase> {
  const name = "unsupported schema version is rejected";
  try {
    const json = `{"schema":99,"channelId":"ch","messageId":"m1","action":"pin","ts":1}`;
    const bad = PinnedMessageCodec.prefix + b64url(json);
    try {
      PinnedMessageCodec.decode(bad);
      return fail(name, "decode did not throw");
    } catch (e) {
      if (
        e instanceof PinnedMessageCodecError &&
        e.kind === "unsupportedSchema" &&
        e.schemaVersion === 99
      ) {
        return pass(name);
      }
      return fail(
        name,
        `wrong error: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
  } catch (e) {
    return fail(name, `setup threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testEmptyChannelIdRejected(): Promise<TestCase> {
  const name = "empty channelId is rejected";
  try {
    const json = `{"schema":1,"channelId":"","messageId":"m1","action":"pin","ts":1}`;
    const bad = PinnedMessageCodec.prefix + b64url(json);
    try {
      PinnedMessageCodec.decode(bad);
      return fail(name, "decode did not throw");
    } catch (e) {
      if (e instanceof PinnedMessageCodecError && e.kind === "malformed") {
        return pass(name);
      }
      return fail(
        name,
        `wrong error: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
  } catch (e) {
    return fail(name, `setup threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testEmptyMessageIdRejected(): Promise<TestCase> {
  const name = "empty messageId is rejected";
  try {
    const json = `{"schema":1,"channelId":"ch","messageId":"","action":"pin","ts":1}`;
    const bad = PinnedMessageCodec.prefix + b64url(json);
    try {
      PinnedMessageCodec.decode(bad);
      return fail(name, "decode did not throw");
    } catch (e) {
      if (e instanceof PinnedMessageCodecError && e.kind === "malformed") {
        return pass(name);
      }
      return fail(
        name,
        `wrong error: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
  } catch (e) {
    return fail(name, `setup threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// ---------------------------------------------------------------------------
// B. Store mechanics
// ---------------------------------------------------------------------------

async function testPinThenIsPinned(): Promise<TestCase> {
  const name = "pin then isPinned=true";
  const s = freshStore();
  try {
    const changed = await s.pin("ch", "m1", 100);
    const pinned = await s.isPinned("ch", "m1");
    if (!changed || !pinned) {
      return fail(name, `changed=${changed} isPinned=${pinned}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testUnpinClearsIsPinned(): Promise<TestCase> {
  const name = "unpin clears isPinned";
  const s = freshStore();
  try {
    await s.pin("ch", "m1", 100);
    const changed = await s.unpin("ch", "m1", 200);
    const pinned = await s.isPinned("ch", "m1");
    if (!changed || pinned) {
      return fail(name, `changed=${changed} isPinned=${pinned}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testRePinIsIdempotent(): Promise<TestCase> {
  const name = "re-pin is idempotent (does not bump observedAt)";
  const s = freshStore();
  try {
    await s.pin("ch", "m1", 100, 1_000);
    const firstObserved = (await s.pinnedMessageIds("ch"))[0]?.observedAt;

    // Same ts → monotonic gate must reject (lastTs === payload.ts fails strict >)
    const changed = await s.pin("ch", "m1", 100, 999_000);
    const secondObserved = (await s.pinnedMessageIds("ch"))[0]?.observedAt;

    if (changed || firstObserved !== secondObserved || firstObserved !== 1_000) {
      return fail(
        name,
        `changed=${changed} first=${firstObserved} second=${secondObserved}`,
      );
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testOutOfOrderUnpinRejected(): Promise<TestCase> {
  const name = "out-of-order unpin (smaller ts) is rejected";
  const s = freshStore();
  try {
    await s.pin("ch", "m1", 100);
    const stale: PinnedMessagePayload = {
      schema: 1,
      channelId: "ch",
      messageId: "m1",
      action: "unpin",
      ts: 50,
    };
    const changed = await s.record(stale);
    const pinned = await s.isPinned("ch", "m1");
    if (changed || !pinned) {
      return fail(name, `changed=${changed} isPinned=${pinned}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testOutOfOrderPinReplayDoesNotBumpObservedAt(): Promise<TestCase> {
  const name = "out-of-order pin replay does not bump observedAt";
  const s = freshStore();
  try {
    await s.pin("ch", "m1", 100, 1_000);
    const firstObserved = (await s.pinnedMessageIds("ch"))[0]?.observedAt;

    const stale: PinnedMessagePayload = {
      schema: 1,
      channelId: "ch",
      messageId: "m1",
      action: "pin",
      ts: 50,
    };
    await s.record(stale, 999_000);
    const secondObserved = (await s.pinnedMessageIds("ch"))[0]?.observedAt;

    if (firstObserved !== secondObserved || firstObserved !== 1_000) {
      return fail(
        name,
        `first=${firstObserved} second=${secondObserved}`,
      );
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testMultipleChannelsTrackedIndependently(): Promise<TestCase> {
  const name = "multiple channels tracked independently";
  const s = freshStore();
  try {
    await s.pin("chA", "m1", 100);
    const okA = await s.isPinned("chA", "m1");
    const okB = await s.isPinned("chB", "m1");
    if (!okA || okB) {
      return fail(name, `chA=${okA} chB=${okB}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testTwoMessagesInSameChannelAreIndependent(): Promise<TestCase> {
  const name = "two messages in same channel are independent";
  const s = freshStore();
  try {
    await s.pin("ch", "m1", 100);
    await s.pin("ch", "m2", 101);
    await s.unpin("ch", "m1", 200);
    const m1 = await s.isPinned("ch", "m1");
    const m2 = await s.isPinned("ch", "m2");
    if (m1 || !m2) {
      return fail(name, `m1=${m1} m2=${m2}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testClearChannelScopedToOneChannel(): Promise<TestCase> {
  const name = "clearChannel is scoped to one channel";
  const s = freshStore();
  try {
    await s.pin("chA", "m1", 100);
    await s.pin("chB", "m2", 101);
    await s.clearChannel("chA");
    const chA = await s.isPinned("chA", "m1");
    const chB = await s.isPinned("chB", "m2");
    if (chA || !chB) {
      return fail(name, `chA=${chA} chB=${chB}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testResetAllClearsEverything(): Promise<TestCase> {
  const name = "resetAll clears everything";
  const s = freshStore();
  try {
    await s.pin("chA", "m1", 100);
    await s.pin("chB", "m2", 101);
    await s.resetAll();
    const chA = await s.isPinned("chA", "m1");
    const chB = await s.isPinned("chB", "m2");
    if (chA || chB) {
      return fail(name, `chA=${chA} chB=${chB}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testPersistenceAcrossInstances(): Promise<TestCase> {
  const name = "pinned state persists across store instances";
  const dbName = `kaether-pintest-persist-${crypto.randomUUID()}`;
  try {
    const writer = namedStore(dbName);
    await writer.pin("ch", "m1", 100);

    // Brand-new store instance pointed at the same IDB — state must survive.
    const reader = namedStore(dbName);
    const pinned = await reader.isPinned("ch", "m1");
    const count = (await reader.pinnedMessageIds("ch")).length;
    if (!pinned || count !== 1) {
      return fail(name, `isPinned=${pinned} count=${count}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  } finally {
    await dropDb(dbName);
  }
}

// ---------------------------------------------------------------------------
// C. Server-rebind resistance
// ---------------------------------------------------------------------------

async function testForgedChannelIdScopedToThatChannel(): Promise<TestCase> {
  const name = "forged channelId scoped to that channel only";
  const s = freshStore();
  try {
    const forged: PinnedMessagePayload = {
      schema: 1,
      channelId: "fake-ch",
      messageId: "m1",
      action: "pin",
      ts: 100,
    };
    await s.record(forged);
    const inFake = await s.isPinned("fake-ch", "m1");
    const inReal = await s.isPinned("real-ch", "m1");
    const inAnother = await s.isPinned("another-ch", "m1");
    if (!inFake || inReal || inAnother) {
      return fail(name, `fake=${inFake} real=${inReal} another=${inAnother}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testStaleUnpinIgnored(): Promise<TestCase> {
  const name = "stale unpin (ts < recorded pin ts) is ignored";
  const s = freshStore();
  try {
    await s.pin("ch", "m1", 100);
    const stale: PinnedMessagePayload = {
      schema: 1,
      channelId: "ch",
      messageId: "m1",
      action: "unpin",
      ts: 50,
    };
    const changed = await s.record(stale);
    const pinned = await s.isPinned("ch", "m1");
    if (changed || !pinned) {
      return fail(name, `changed=${changed} isPinned=${pinned}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testFiftyFirstPinEvictsOldest(): Promise<TestCase> {
  const name = "51st pin evicts the oldest by pinnedTs";
  const s = freshStore();
  try {
    // Pin 50 messages with strictly increasing ts.
    for (let i = 0; i < MAX_PINS_PER_CHANNEL; i++) {
      await s.pin("ch", `m${i}`, 1_000 + i);
    }
    const beforeCount = (await s.pinnedMessageIds("ch")).length;

    // 51st pin has the highest ts — m0 (oldest by pinnedTs=1000) must be evicted.
    await s.pin("ch", "m50", 9_999);
    const after = await s.pinnedMessageIds("ch");

    const stillHasNewest = after.some((r) => r.messageId === "m50");
    const evictedOldest = !after.some((r) => r.messageId === "m0");
    const cappedAtFifty = after.length === MAX_PINS_PER_CHANNEL;

    if (
      beforeCount !== MAX_PINS_PER_CHANNEL ||
      !stillHasNewest ||
      !evictedOldest ||
      !cappedAtFifty
    ) {
      return fail(
        name,
        `before=${beforeCount} after=${after.length} hasM50=${stillHasNewest} evictedM0=${evictedOldest}`,
      );
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// ---------------------------------------------------------------------------
// D. Sorting + listing
// ---------------------------------------------------------------------------

async function testPinnedListSortedDescending(): Promise<TestCase> {
  const name = "pinnedMessageIds sorted descending by pinnedTs";
  const s = freshStore();
  try {
    await s.pin("ch", "m1", 100);
    await s.pin("ch", "m2", 300);
    await s.pin("ch", "m3", 200);
    const list = await s.pinnedMessageIds("ch");
    const tsList = list.map((r) => r.pinnedTs);
    const ids = list.map((r) => r.messageId);
    if (
      tsList[0] !== 300 ||
      tsList[1] !== 200 ||
      tsList[2] !== 100 ||
      ids[0] !== "m2" ||
      ids[1] !== "m3" ||
      ids[2] !== "m1"
    ) {
      return fail(name, `ts=${JSON.stringify(tsList)} ids=${JSON.stringify(ids)}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testListContainsOnlyCurrentlyPinned(): Promise<TestCase> {
  const name = "list contains only currently-pinned messages";
  const s = freshStore();
  try {
    await s.pin("ch", "m1", 100);
    await s.pin("ch", "m2", 200);
    await s.pin("ch", "m3", 300);
    await s.unpin("ch", "m2", 400);
    const list = await s.pinnedMessageIds("ch");
    const ids = new Set(list.map((r) => r.messageId));
    if (list.length !== 2 || !ids.has("m1") || !ids.has("m3") || ids.has("m2")) {
      return fail(
        name,
        `ids=${JSON.stringify([...ids].sort())} count=${list.length}`,
      );
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// ---------------------------------------------------------------------------
// Wire-format prefix validation
// ---------------------------------------------------------------------------

async function testWireFormatPrefixIsKaetherPinV1(): Promise<TestCase> {
  const name = 'wire-format prefix is "kaether:pin:v1:"';
  try {
    const p: PinnedMessagePayload = {
      schema: 1,
      channelId: "ch",
      messageId: "m",
      action: "pin",
      ts: 1,
    };
    const encoded = PinnedMessageCodec.encode(p);
    if (!encoded.startsWith("kaether:pin:v1:")) {
      return fail(name, `encoded starts with: ${encoded.slice(0, 30)}`);
    }
    // Must NOT use the stoat: legacy namespace.
    if (encoded.startsWith("stoat:")) {
      return fail(name, "prefix incorrectly uses stoat: namespace");
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Pinned messages (cross-device sync)",
  isVerified: true,
  run: async () => {
    const cases: TestCase[] = await Promise.all([
      // A. Codec
      testCodecRoundTripPin(),
      testCodecRoundTripUnpin(),
      testIsPinEventDistinguishesOtherEnvelopes(),
      testTryDecodeReturnsNilForUserMessage(),
      testMalformedBase64Rejected(),
      testUnsupportedSchemaRejected(),
      testEmptyChannelIdRejected(),
      testEmptyMessageIdRejected(),
      // B. Store mechanics
      testPinThenIsPinned(),
      testUnpinClearsIsPinned(),
      testRePinIsIdempotent(),
      testOutOfOrderUnpinRejected(),
      testOutOfOrderPinReplayDoesNotBumpObservedAt(),
      testMultipleChannelsTrackedIndependently(),
      testTwoMessagesInSameChannelAreIndependent(),
      testClearChannelScopedToOneChannel(),
      testResetAllClearsEverything(),
      testPersistenceAcrossInstances(),
      // C. Server-rebind resistance
      testForgedChannelIdScopedToThatChannel(),
      testStaleUnpinIgnored(),
      testFiftyFirstPinEvictsOldest(),
      // D. Sorting + listing
      testPinnedListSortedDescending(),
      testListContainsOnlyCurrentlyPinned(),
      // Wire-format guard
      testWireFormatPrefixIsKaetherPinV1(),
    ]);
    return buildSuiteResult(cases);
  },
});
